import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { fetchOrderDetails, clearOrderDetails } from '../../redux/slices/orderSlice';
import axios from 'axios';
import { Clock } from 'lucide-react';
import CancellationModal from './CancellationModal';
import TrackingTimeline from './TrackingTimeline';

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';

const OrderDetailsPage = () => {
  const { id }      = useParams();
  const dispatch    = useDispatch();
  const { orderDetails, loading, error } = useSelector((s) => s.orders);

  const [returnInfo,             setReturnInfo]             = useState(null);
  const [loadingReturn,          setLoadingReturn]          = useState(false);
  const [showCancellationModal,  setShowCancellationModal]  = useState(false);
  const [timeRemaining,          setTimeRemaining]          = useState(null);
  const [canCancel,              setCanCancel]              = useState(false);

  useEffect(() => {
    dispatch(clearOrderDetails());
    if (id) {
      dispatch(fetchOrderDetails(id));
      fetchReturnInfo(id);
    }
  }, [dispatch, id]);

  // Cancellation countdown
  useEffect(() => {
    if (!orderDetails) return;
    const check = () => {
      if (orderDetails.status !== 'Processing') { setCanCancel(false); setTimeRemaining(null); return; }
      const remaining = 2 * 60 * 60 * 1000 - (Date.now() - new Date(orderDetails.createdAt).getTime());
      if (remaining > 0) { setCanCancel(true); setTimeRemaining(remaining); }
      else { setCanCancel(false); setTimeRemaining(null); }
    };
    check();
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, [orderDetails]);

  const fetchReturnInfo = async (orderId) => {
    try {
      setLoadingReturn(true);
      const { data } = await axios.get(`${API_BASE}/api/returns/order/${orderId}`);
      setReturnInfo(data);
    } catch (err) {
      if (err.response?.status !== 404) console.error('Failed to fetch return info:', err);
    } finally {
      setLoadingReturn(false);
    }
  };

  const getPaymentStatusDisplay = (order) => {
    if (order.paymentStatus) return order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1);
    return order.isPaid ? 'Paid' : 'Unpaid';
  };

  const formatTimeRemaining = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${h}h ${m}m ${s}s`;
  };

  const handleCancellationSuccess = () => dispatch(fetchOrderDetails(id));

  const isPaid = orderDetails?.isPaid ||
    orderDetails?.paymentStatus === 'paid' ||
    orderDetails?.paymentStatus === 'completed';

  /* ── LOADING ── */
  if (loading) return (
    <>
      <style>{odStyles}</style>
      <div className="od-root">
        <div className="od-state">
          <span className="od-state-label">Loading Order</span>
        </div>
      </div>
    </>
  );

  /* ── ERROR ── */
  if (error) return (
    <>
      <style>{odStyles}</style>
      <div className="od-root">
        <div className="od-state">
          <span className="od-state-label">Something went wrong</span>
          <span className="od-state-sub">{error}</span>
          <Link to="/my-orders" className="od-back-link" style={{ marginTop: '16px' }}>← Back to Orders</Link>
        </div>
      </div>
    </>
  );

  if (!orderDetails) return (
    <>
      <style>{odStyles}</style>
      <div className="od-root">
        <div className="od-state">
          <span className="od-state-label">Order not found</span>
          <Link to="/my-orders" className="od-back-link" style={{ marginTop: '16px' }}>← Back to Orders</Link>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{odStyles}</style>

      <div className="od-root">

        {/* ── HEADER ── */}
        <div className="od-header">
          <div>
            <span className="od-eyebrow">Account</span>
            <h1 className="od-title">Order <span className="od-title-bold">Details</span></h1>
          </div>
          <Link to="/my-orders" className="od-back-link">← Back to Orders</Link>
        </div>

        <div className="od-rule-wrap"><hr className="od-hr" /></div>

        {/* ── ORDER META BAR ── */}
        <div className="od-meta-bar">
          <div className="od-meta-left">
            <span className="od-order-id">#{orderDetails._id.slice(-10).toUpperCase()}</span>
            <span className="od-order-date">
              {new Date(orderDetails.createdAt).toLocaleDateString('en-IN', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
              })}
            </span>
          </div>
          <div className="od-badges">
            <span className={`od-badge ${
              orderDetails.status === 'Delivered'  ? 'od-badge--delivered' :
              orderDetails.status === 'Shipped'    ? 'od-badge--shipped' :
              orderDetails.status === 'Cancelled'  ? 'od-badge--cancelled' :
              'od-badge--processing'
            }`}>
              {orderDetails.status}
            </span>
            <span className={`od-badge ${isPaid ? 'od-badge--paid' : 'od-badge--unpaid'}`}>
              {getPaymentStatusDisplay(orderDetails)}
            </span>
          </div>
        </div>

        <div className="od-rule-wrap od-rule-wrap--sm"><hr className="od-hr" /></div>

        {/* ── CANCELLATION COUNTDOWN ── */}
        {orderDetails.status === 'Processing' && canCancel && timeRemaining && (
          <div className="od-alert od-alert--warning">
            <div className="od-alert-left">
              <div className="od-alert-title">
                <Clock size={13} />
                Cancellation Window Open
              </div>
              <p className="od-alert-text">
                You can cancel this order within the next <strong>{formatTimeRemaining(timeRemaining)}</strong>
              </p>
            </div>
            <button
              className="od-cancel-btn"
              onClick={() => setShowCancellationModal(true)}
            >
              Cancel Order
            </button>
          </div>
        )}

        {/* ── RETURN INFO ── */}
        {returnInfo && (
          <div className="od-alert od-alert--info">
            <div className="od-alert-title">Return Request</div>
            <div className="od-return-row">
              <span className={`od-badge od-badge--sm ${
                returnInfo.status === 'Approved' || returnInfo.status === 'Refunded'
                  ? 'od-badge--delivered'
                  : returnInfo.status === 'Rejected' || returnInfo.status === 'Cancelled'
                  ? 'od-badge--cancelled'
                  : 'od-badge--processing'
              }`}>
                {returnInfo.status}
              </span>
              {returnInfo.reason && (
                <span className="od-return-reason">Reason: {returnInfo.reason}</span>
              )}
            </div>
          </div>
        )}

        {/* ── CANCELLED ── */}
        {orderDetails.status === 'Cancelled' && orderDetails.cancellationReason && (
          <div className="od-alert od-alert--error">
            <div className="od-alert-title">✕ Order Cancelled</div>
            <p className="od-alert-text"><strong>Reason:</strong></p>
            <p className="od-alert-reason">{orderDetails.cancellationReason}</p>
            {orderDetails.isPaid && (
              <p className="od-alert-text" style={{ marginTop: '10px' }}>
                <strong>Refund Status:</strong> Your refund will be processed within 5–7 business days.
              </p>
            )}
          </div>
        )}

        {/* ── DELIVERED ── */}
        {orderDetails.status === 'Delivered' && (
          <div className="od-alert od-alert--success">
            <div className="od-alert-title">✓ Delivered Successfully</div>
            <p className="od-alert-text">
              {new Date(orderDetails.deliveredAt).toLocaleString('en-IN', {
                weekday: 'long', year: 'numeric', month: 'long',
                day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </p>
          </div>
        )}

        {/* ── TRACKING ── */}
        {['Shipped', 'Delivered'].includes(orderDetails.status) && orderDetails.trackingNumber && (
          <div className="od-tracking">
            <TrackingTimeline
              awb={orderDetails.trackingNumber}
              courierName={orderDetails.courierName}
              orderStatus={orderDetails.status}
              orderDates={{
                createdAt:   orderDetails.createdAt,
                shippedAt:   orderDetails.shippedAt,
                deliveredAt: orderDetails.deliveredAt,
              }}
            />
          </div>
        )}

        {/* ── INFO GRID ── */}
        <div className="od-info-grid">

          {/* Order Info */}
          <div className="od-info-block">
            <span className="od-info-heading">Order Information</span>
            <div className="od-info-rows">
              <div className="od-info-row">
                <span className="od-info-label">Total Amount</span>
                <span className="od-info-value">₹{orderDetails.totalPrice?.toLocaleString() || '0'}</span>
              </div>
              <div className="od-info-row">
                <span className="od-info-label">Payment Method</span>
                <span className="od-info-value">{orderDetails.paymentMethod}</span>
              </div>
              {orderDetails.paidAt && (
                <div className="od-info-row">
                  <span className="od-info-label">Paid At</span>
                  <span className="od-info-value">{new Date(orderDetails.paidAt).toLocaleString('en-IN')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Shipped To */}
          <div className="od-info-block">
            <span className="od-info-heading">Shipped To</span>
            <div className="od-info-rows">
              <div className="od-info-row">
                <span className="od-info-label">Name</span>
                <span className="od-info-value">
                  {orderDetails.shippingAddress?.firstName
                    ? [orderDetails.shippingAddress.firstName, orderDetails.shippingAddress.lastName].filter(Boolean).join(' ')
                    : orderDetails.user?.name || 'N/A'}
                </span>
              </div>
              <div className="od-info-row">
                <span className="od-info-label">Mobile</span>
                <span className="od-info-value">
                  {orderDetails.shippingAddress?.phone || orderDetails.shippingAddress?.mobile || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Billed By */}
          <div className="od-info-block">
            <span className="od-info-heading">Billed By</span>
            <div className="od-info-rows">
              <div className="od-info-row">
                <span className="od-info-label">Name</span>
                <span className="od-info-value">{orderDetails.user?.name || 'N/A'}</span>
              </div>
              <div className="od-info-row">
                <span className="od-info-label">Email</span>
                <span className="od-info-value od-info-value--sm">{orderDetails.user?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Shipping Info */}
          {orderDetails.shippingAddress && (
            <div className="od-info-block">
              <span className="od-info-heading">Shipping Address</span>
              <div className="od-info-rows">
                <div className="od-info-row">
                  <span className="od-info-label">Address</span>
                  <span className="od-info-value">{orderDetails.shippingAddress.address}</span>
                </div>
                <div className="od-info-row">
                  <span className="od-info-label">City</span>
                  <span className="od-info-value">{orderDetails.shippingAddress.city}</span>
                </div>
                <div className="od-info-row">
                  <span className="od-info-label">State</span>
                  <span className="od-info-value">{orderDetails.shippingAddress.state}</span>
                </div>
                <div className="od-info-row">
                  <span className="od-info-label">Postal Code</span>
                  <span className="od-info-value">{orderDetails.shippingAddress.postalCode}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="od-rule-wrap"><hr className="od-hr" /></div>

        {/* ── ORDER ITEMS ── */}
        <div className="od-items-section">
          <div className="od-items-header">
            <span className="od-panel-eyebrow">Items</span>
            <h2 className="od-items-title">
              Order <span className="od-title-bold">Items</span>
              <span className="od-items-count"> · {orderDetails.orderItems?.length || 0}</span>
            </h2>
          </div>

          {/* Desktop table */}
          <div className="od-table-wrap">
            <table className="od-table">
              <thead>
                <tr className="od-thead-row">
                  <th className="od-th">Product</th>
                  <th className="od-th">Details</th>
                  <th className="od-th od-th--right">Unit Price</th>
                  <th className="od-th od-th--center">Qty</th>
                  <th className="od-th od-th--right">Total</th>
                </tr>
              </thead>
              <tbody>
                {orderDetails.orderItems?.map((item, index) => (
                  <tr key={index} className="od-item-row">
                    <td className="od-td">
                      <div className="od-product-cell">
                        <img src={item.image} alt={item.name} className="od-product-thumb" />
                        <Link to={`/product/${item.productId}`} className="od-product-link">
                          {item.name}
                        </Link>
                        {item.freeQuantity > 0 && (
                          <span className="od-free-tag">{item.freeQuantity} Free</span>
                        )}
                      </div>
                    </td>
                    <td className="od-td">
                      <span className="od-item-meta">
                        {item.size && `${item.size}`}
                        {item.size && item.color && ' · '}
                        {item.color && `${item.color}`}
                      </span>
                    </td>
                    <td className="od-td od-td--right">
                      <span className="od-item-price">₹{item.price?.toLocaleString()}</span>
                    </td>
                    <td className="od-td od-td--center">
                      <span className="od-item-qty">{item.quantity}</span>
                    </td>
                    <td className="od-td od-td--right">
                      <span className="od-item-total">₹{(item.price * item.quantity)?.toLocaleString()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="od-mobile-items">
            {orderDetails.orderItems?.map((item, index) => (
              <div key={index} className="od-mobile-item">
                <img src={item.image} alt={item.name} className="od-mobile-thumb" />
                <div className="od-mobile-info">
                  <Link to={`/product/${item.productId}`} className="od-product-link">
                    {item.name}
                  </Link>
                  {item.freeQuantity > 0 && (
                    <span className="od-free-tag">{item.freeQuantity} Free</span>
                  )}
                  {(item.size || item.color) && (
                    <span className="od-item-meta" style={{ marginTop: '4px' }}>
                      {item.size && item.size}
                      {item.size && item.color && ' · '}
                      {item.color && item.color}
                    </span>
                  )}
                  <span className="od-item-meta">Qty · {item.quantity}</span>
                </div>
                <span className="od-item-total">₹{(item.price * item.quantity)?.toLocaleString()}</span>
              </div>
            ))}
          </div>

          {/* Order total */}
          {orderDetails.discountTotal > 0 && (
            <div className="od-discount-block">
              <div className="od-discount-row">
                <span className="od-discount-label">Subtotal</span>
                <span className="od-discount-value">₹{orderDetails.subtotal?.toLocaleString()}</span>
              </div>
              <div className="od-discount-row">
                <span className="od-discount-label">{orderDetails.offer?.name || 'Offer'}</span>
                <span className="od-discount-value">— ₹{orderDetails.discountTotal?.toLocaleString()}</span>
              </div>
            </div>
          )}
          <div className="od-order-total">
            <span className="od-total-label">Order Total</span>
            <span className="od-total-value">₹{orderDetails.totalPrice?.toLocaleString()}</span>
          </div>
        </div>

      </div>

      <CancellationModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        orderId={orderDetails._id}
        onSuccess={handleCancellationSuccess}
      />
    </>
  );
};

/* ── STYLES ── */
const odStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');
  .od-mobile-items { display: none; }

@media (max-width: 768px) {
  .od-table-wrap  { display: none; }
  .od-mobile-items { display: flex; }
}

  .od-root {
    background: #f8f8f8;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
    padding: 64px 80px 96px;
    max-width: 1400px;
    margin: 0 auto;
    box-sizing: border-box;
  }

  /* ── STATE ── */
  .od-state {
    display: flex; flex-direction: column;
    align-items: center; gap: 8px;
    padding: 80px 0;
  }
  .od-state-label {
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.35em; text-transform: uppercase;
    color: black;
  }
  .od-state-sub {
    font-size: 10px; font-weight: 300;
    letter-spacing: 0.15em; color: black;
  }

  /* ── HEADER ── */
  .od-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 32px;
    gap: 16px;
  }

  .od-eyebrow {
    display: flex; align-items: center; gap: 12px;
    font-size: 9px; font-weight: 400;
    letter-spacing: 0.5em; text-transform: uppercase;
    color: black;
    margin-bottom: 12px;
  }
  .od-eyebrow::before {
    content: '';
    width: 24px; height: 1px;
    background: black;
    display: block; flex-shrink: 0;
  }

  .od-title {
    font-size: clamp(28px, 4vw, 52px);
    font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase;
    line-height: 1; margin: 0;
  }
  .od-title-bold { font-weight: 600; }

  .od-back-link {
    font-size: 9px; font-weight: 400;
    letter-spacing: 0.3em; text-transform: uppercase;
    color: black; text-decoration: none;
    padding-bottom: 4px;
    border-bottom: 1px solid rgba(0,0,0,0.15);
    transition: color 0.2s, border-color 0.2s;
    white-space: nowrap;
    align-self: flex-end;
  }
  .od-back-link:hover { color: #000; border-color: #000; }

  /* ── RULE ── */
  .od-rule-wrap { margin-bottom: 32px; }
  .od-rule-wrap--sm { margin-bottom: 24px; }
  .od-hr { border: none; border-top: 1px solid black; margin: 0; }

  /* ── META BAR ── */
  .od-meta-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .od-meta-left {
    display: flex; flex-direction: column; gap: 4px;
  }

  .od-order-id {
    font-size: 13px; font-weight: 500;
    letter-spacing: 0.2em; color: #000;
    font-variant-numeric: tabular-nums;
  }

  .od-order-date {
    font-size: 10px; font-weight: 300;
    letter-spacing: 0.1em; color: black;
  }

  .od-badges { display: flex; gap: 6px; flex-wrap: wrap; }

  /* ── BADGES ── */
  .od-badge {
    font-family: 'Exo 2', sans-serif;
    font-size: 7px; font-weight: 600;
    letter-spacing: 0.3em; text-transform: uppercase;
    padding: 4px 10px; display: inline-block;
  }
  .od-badge--sm { font-size: 6.5px; padding: 3px 8px; }
  .od-badge--delivered  { background: #000; color: #fff; }
  .od-badge--shipped    { background: #000; color: #fff; }
  .od-badge--processing { background: transparent; color: rgba(0,0,0,0.5); border: 1px solid rgba(0,0,0,0.25); }
  .od-badge--cancelled  { background: transparent; color: rgba(0,0,0,0.45); border: 1px solid rgba(0,0,0,0.2); }
  .od-badge--paid       { background: #000; color: #fff; }
  .od-badge--unpaid     { background: transparent; color: rgba(0,0,0,0.4); border: 1px solid rgba(0,0,0,0.18); }

  /* ── ALERTS ── */
  .od-alert {
    padding: 18px 20px;
    margin-bottom: 20px;
    border-left: 3px solid;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .od-alert--warning { border-color: rgba(0,0,0,0.3); background: rgba(0,0,0,0.02); }
  .od-alert--info    { border-color: rgba(0,0,0,0.2); background: rgba(0,0,0,0.02); }
  .od-alert--error   { border-color: rgba(0,0,0,0.35); background: rgba(0,0,0,0.02); }
  .od-alert--success { border-color: #000; background: rgba(0,0,0,0.02); }

  .od-alert-left {
    display: flex; flex-direction: column; gap: 6px;
    flex: 1;
  }

  .od-alert-title {
    font-size: 9px; font-weight: 600;
    letter-spacing: 0.35em; text-transform: uppercase;
    color: #000;
    display: flex; align-items: center; gap: 6px;
    margin-bottom: 6px;
  }

  .od-alert-text {
    font-size: 12px; font-weight: 300;
    letter-spacing: 0.06em; color: black;
    line-height: 1.7; margin: 0;
  }

  .od-alert-reason {
    font-size: 12px; font-weight: 300;
    letter-spacing: 0.04em; color: rgba(0,0,0,0.55);
    line-height: 1.7;
    padding: 10px 14px;
    background: rgba(0,0,0,0.03);
    border: 1px solid rgba(0,0,0,0.08);
    margin-top: 6px;
  }

  .od-return-row {
    display: flex; align-items: center; gap: 10px;
    margin-top: 8px; flex-wrap: wrap;
  }

  .od-return-reason {
    font-size: 11px; font-weight: 300;
    letter-spacing: 0.08em; color: black;
  }

  .od-cancel-btn {
    background: #000; color: #fff;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 600;
    letter-spacing: 0.35em; text-transform: uppercase;
    padding: 10px 18px;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .od-cancel-btn:hover { background: #333; }

  /* ── TRACKING ── */
  .od-tracking { margin-bottom: 32px; }

  /* ── INFO GRID ── */
  .od-info-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
    margin-bottom: 40px;
  }

  .od-info-block {
    padding: 24px;
    background: #f8f8f8;
    border: 1px solid black;
  }

  .od-info-heading {
    display: block;
    font-size: 8px; font-weight: 500;
    letter-spacing: 0.45em; text-transform: uppercase;
    color: black;
    margin-bottom: 16px;
  }

  .od-info-rows { display: flex; flex-direction: column; gap: 0; }

  .od-info-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid black;
  }
  .od-info-row:last-child { border-bottom: none; }

  .od-info-label {
    font-size: 8px; font-weight: 400;
    letter-spacing: 0.3em; text-transform: uppercase;
    color: black; flex-shrink: 0;
  }

  .od-info-value {
    font-size: 11px; font-weight: 300;
    letter-spacing: 0.06em; color: #000;
    text-align: right;
  }
  .od-info-value--sm { font-size: 10px; }

  /* ── ITEMS SECTION ── */
  .od-items-section { margin-top: 40px; }

  .od-items-header { margin-bottom: 24px; }

  .od-panel-eyebrow {
    display: flex; align-items: center; gap: 10px;
    font-size: 8px; font-weight: 400;
    letter-spacing: 0.45em; text-transform: uppercase;
    color: black; margin-bottom: 10px;
  }
  .od-panel-eyebrow::before {
    content: '';
    width: 18px; height: 1px;
    background: black;
    display: block; flex-shrink: 0;
  }

  .od-items-title {
    font-size: clamp(20px, 2.5vw, 32px);
    font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase;
    line-height: 1; margin: 0;
  }

  .od-items-count {
    font-weight: 300;
    color: black;
    font-size: 0.75em;
  }

  /* ── TABLE ── */
  .od-table-wrap { overflow-x: auto; margin-bottom: 0; }

  .od-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Exo 2', sans-serif;
  }

  .od-thead-row { border-bottom: 1px solid black; }

  .od-th {
    font-size: 8px; font-weight: 500;
    letter-spacing: 0.4em; text-transform: uppercase;
    color: black;
    padding: 0 16px 14px 0;
    text-align: left; white-space: nowrap;
  }
  .od-th--right  { text-align: right; padding-right: 0; }
  .od-th--center { text-align: center; }

  .od-item-row {
    border-bottom: 1px solid black;
  }
  .od-item-row:last-child { border-bottom: none; }

  .od-td {
    padding: 18px 16px 18px 0;
    vertical-align: middle;
  }
  .od-td--right  { text-align: right; padding-right: 0; }
  .od-td--center { text-align: center; }

  .od-product-cell {
    display: flex; align-items: center; gap: 14px;
  }

  .od-product-thumb {
    width: 60px; height: 72px;
    object-fit: cover; flex-shrink: 0;
  }

  .od-product-link {
    font-size: 10px; font-weight: 500;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: #000; text-decoration: none;
    line-height: 1.4;
    transition: opacity 0.2s;
  }
  .od-product-link:hover { opacity: 0.6; }

  .od-item-meta {
    display: block;
    font-size: 9px; font-weight: 300;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: black;
  }

  .od-item-price {
    font-size: 12px; font-weight: 300;
    letter-spacing: 0.06em; color: black;
  }

  .od-item-qty {
    font-size: 12px; font-weight: 400;
    letter-spacing: 0.1em; color: #000;
  }

  .od-item-total {
    font-size: 13px; font-weight: 500;
    letter-spacing: 0.06em; color: #000;
  }

  /* ── MOBILE ITEMS ── */
  .od-mobile-items {
    flex-direction: column; gap: 0;
  }

  .od-mobile-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 0;
    border-bottom: 1px solid black;
  }
  .od-mobile-item:last-child { border-bottom: none; }

  .od-mobile-thumb {
    width: 68px; height: 82px;
    object-fit: cover; flex-shrink: 0;
  }

  .od-mobile-info {
    flex: 1;
    display: flex; flex-direction: column; gap: 4px;
    min-width: 0;
  }

  /* ── ORDER TOTAL ── */
  .od-free-tag { display: inline-block; background: #000; color: #fff; font-size: 7px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase; padding: 3px 6px; margin-top: 2px; align-self: flex-start; }
  .od-discount-block { margin-top: 20px; display: flex; flex-direction: column; gap: 8px; }
  .od-discount-row { display: flex; justify-content: space-between; align-items: baseline; }
  .od-discount-label { font-size: 8px; font-weight: 400; letter-spacing: 0.3em; text-transform: uppercase; color: black; }
  .od-discount-value { font-size: 11px; font-weight: 400; letter-spacing: 0.1em; color: black; }
  .od-order-total {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 20px 0 0;
    border-top: 1px solid black;
    margin-top: 16px;
  }

  .od-total-label {
    font-size: 9px; font-weight: 500;
    letter-spacing: 0.4em; text-transform: uppercase;
    color: black;
  }

  .od-total-value {
    font-size: 22px; font-weight: 300;
    letter-spacing: 0.04em; color: #000;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .od-root { padding: 48px 40px 80px; }
    .od-info-grid { grid-template-columns: 1fr 1fr; }
  }

  @media (max-width: 768px) {
    .od-root { padding: 40px 20px 64px; }
    .od-header { flex-direction: column; align-items: flex-start; gap: 16px; }
    .od-meta-bar { flex-direction: column; align-items: flex-start; }
    .od-info-grid { grid-template-columns: 1fr; gap: 8px; }
    .od-rule-wrap { margin-bottom: 24px; }
  }

  @media (max-width: 360px) {
    .od-root { padding: 32px 14px 48px; }
  }
`;

export default OrderDetailsPage;
